import { useQuery } from "@apollo/client";
import { ProfileSkeleton } from "@/components/Skeleton";
import { Person } from "@/types";
import { renderName } from "@/lib/personHelpers";
import {
  getProgramName,
  getPrimaryFacilityName,
  getPrimaryEmploymentTitle,
  getPrimarySectionName,
  getPrimaryFacilityLocation,
  getPrimaryJobClassification
} from "@/pages/people/_utils";
import { PERSON_DETAILS_HEADER_MEMBERSHIP_QUERY, PERSON_DETAILS_HEADER_QUERY } from "@/features/person/queries";
import ObjectHeading from "@/components/ObjectHeading";
import { DOMAIN, PERMISSION, useHasCapabilityBool } from "@/hooks/useCapabilities";

export interface MobilePersonOverviewProps {
  pgaId: string;
}

type PersonData = { person: Person };

export default function MobilePersonOverview({ pgaId }: MobilePersonOverviewProps) {
  const canViewPersonMembership = useHasCapabilityBool({
    domain: DOMAIN.PEOPLE,
    permission: PERMISSION.viewPersonMembership
  });

  const { data, loading, error } = useQuery<PersonData>(PERSON_DETAILS_HEADER_QUERY, {
    variables: { pgaId }
  });

  const { data: membershipData, loading: membershipLoading } = useQuery<PersonData>(
    PERSON_DETAILS_HEADER_MEMBERSHIP_QUERY,
    {
      variables: { pgaId },
      skip: !canViewPersonMembership,
      fetchPolicy: "network-only"
    }
  );

  const membershipChromePending = canViewPersonMembership && membershipLoading;

  if (loading || membershipChromePending) return <ProfileSkeleton />;
  if (error) return <div>Error loading person details</div>;
  if (!data?.person) return <div>Person not found</div>;

  const basePerson = data.person;
  const membershipPerson = membershipData?.person;
  const person = membershipPerson
    ? {
        ...basePerson,
        latestProgram: membershipPerson.latestProgram,
        ccmsSuspensions: membershipPerson.ccmsSuspensions
      }
    : basePerson;

  const { profilePhoto, displayName, informalName, preferredLastName, suffixName, middleName, firstName } = person;

  const personName = renderName(displayName, informalName, preferredLastName, suffixName, middleName, firstName);
  const memberType = getProgramName(person);
  const sectionName = getPrimarySectionName(person);
  const facility = getPrimaryFacilityName(person);
  const facilityLocation = getPrimaryFacilityLocation(person);
  const jobTitle = getPrimaryEmploymentTitle(person);
  const jobClassification = getPrimaryJobClassification(person);

  // Combine program type and section for display
  const programAndSection = [memberType, sectionName].filter(Boolean).join(", ");

  // Combine facility name with location
  const facilityWithLocation = [facility, facilityLocation].filter(Boolean).join(", ");

  // Combine job title with classification
  const jobWithClassification = jobTitle && jobClassification ? `${jobTitle} (${jobClassification})` : jobTitle;

  return (
    <div
      className="bg-sys-brand-surface pb-4 border-b border-sys-brand-outline-variant"
      data-testid="mobile-person-overview"
    >
      <ObjectHeading
        title={personName}
        imageUrl={profilePhoto}
        iconName="person"
        variant="landing-page"
        metaData={[
          ...(programAndSection ? [{ text: programAndSection, boldValue: true }] : []),
          { text: pgaId, label: "PGA ID", copyable: true },
          ...(facilityWithLocation ? [{ text: facilityWithLocation, label: "Facility" }] : []),
          ...(jobWithClassification ? [{ text: jobWithClassification, label: "Job" }] : [])
        ]}
      />
    </div>
  );
}
